import React, { useContext, useState } from "react";
import { IonPage, IonContent, IonHeader, IonToolbar, IonButtons, IonButton, IonModal, IonIcon } from "@ionic/react";
import { useHistory, useLocation } from "react-router-dom";
import { ProfileContext } from "../../context/ProfileContext";
import { cloudDownload, cloudDownloadOutline, download, downloadOutline } from "ionicons/icons";

interface WebsiteLayoutProps {
  children: React.ReactNode;
}

export const WebsiteLayout: React.FC<WebsiteLayoutProps> = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const { usersID } = useContext(ProfileContext);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  // Check if user is authenticated
  const isAuthenticated = !!usersID;
  const isLandingPage = location.pathname === "/";

  const handlePortalClick = () => {
    if (isAuthenticated) {
      history.push("/portal");
    } else {
      history.push("/login?returnUrl=/portal");
    }
  };

  const openStoreLink = (url: string) => {
    setShowDownloadModal(false);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          style={{
            "--background": "#1a1a1a",
            "--color": "#ffffff",
            "--border-color": "#333",
            "--min-height": "58px",
          }}
        >
          <IonButtons slot="start" style={{ minWidth: 0, flexShrink: 1 }}>
            <IonButton
              fill="clear"
              onClick={() => history.push("/")}
              aria-label="WerkApp home"
              style={{
                "--padding-start": "8px",
                "--padding-end": "8px",
                minWidth: 0,
                margin: 0,
              }}
            >
              <img
                src="/assets/WerkAppYellow.png"
                alt=""
                style={{
                  height: 34,
                  width: 34,
                  flex: "0 0 34px",
                  objectFit: "cover",
                  borderRadius: 8,
                }}
              />

              <span className="website-header-brand-name">WerkApp</span>
            </IonButton>
          </IonButtons>

          <IonButtons
            slot="end"
            style={{
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            {isLandingPage && (
              <IonButton
                onClick={() => setShowDownloadModal(true)}
                aria-label="Get the App"
                className="get-app-header-button"
                style={{
                  "--color": "#f4b41a",
                  "--padding-start": "6px",
                  "--padding-end": "6px",
                  marginInline: 0,
                }}
              >
                <IonIcon size="large" icon={downloadOutline} />
              </IonButton>
            )}

            <IonButton
              onClick={() => history.push("/about")}
              className="website-header-nav-button"
              style={{ "--color": "#ffffff" }}
            >
              About
            </IonButton>

            <IonButton
              onClick={() => history.push("/privacy")}
              className="website-header-nav-button"
              style={{ "--color": "#ffffff" }}
            >
              Privacy
            </IonButton>

            {/* Scheduler Login temporarily disabled
      <IonButton
        onClick={handlePortalClick}
        fill="outline"
        className="desktop-only-portal-link"
        style={{
          '--border-color': '#f4b41a',
          '--color': '#f4b41a',
        }}
      >
        Scheduler Login
      </IonButton>
      */}
          </IonButtons>
        </IonToolbar>

        <style>
          {`
      .website-header-brand-name {
        margin-left: 10px;
        color: #ffffff;
        font-size: 1.5rem;
        font-weight: bold;
        white-space: nowrap;
      }

      .website-header-nav-button,
      .get-app-header-button {
        flex-shrink: 0;
      }

      @media (max-width: 600px) {
        .website-header-brand-name {
          font-size: 1.15rem;
          margin-left: 7px;
        }

        .website-header-nav-button {
          font-size: 0.82rem !important;
          --padding-start: 6px;
          --padding-end: 6px;
          margin-inline: 0;
        }

        .get-app-header-button {
          --padding-start: 4px;
          --padding-end: 4px;
        }
      }

      @media (max-width: 440px) {
        .website-header-brand-name {
          display: none;
        }

        .website-header-nav-button {
          font-size: 0.76rem !important;
          --padding-start: 4px;
          --padding-end: 4px;
        }
      }

      @media (max-width: 350px) {
        .website-header-nav-button {
          font-size: 0.7rem !important;
          --padding-start: 2px;
          --padding-end: 2px;
        }

        .get-app-header-button {
          --padding-start: 2px;
          --padding-end: 2px;
        }
      }
    `}
        </style>
      </IonHeader>

      <IonContent className="website-content">
        <IonModal
          isOpen={showDownloadModal}
          onDidDismiss={() => setShowDownloadModal(false)}
          className="download-app-modal"
        >
          <div
            style={{
              padding: "1.5rem",
              backgroundColor: "#ffffff",
              borderRadius: "12px",
            }}
          >
            <h3 style={{ margin: "0 0 0.5rem", color: "#1a1a1a" }}>Download WerkApp</h3>
            <p style={{ margin: "0 0 1rem", color: "#666" }}>Choose your platform:</p>
            <div style={{ display: "grid", gap: "0.75rem" }}>
              <IonButton
                expand="block"
                onClick={() => openStoreLink("https://apps.apple.com/us/app/werkapp/id6446168426")}
                style={{ "--background": "#1a1a1a", "--color": "#ffffff" }}
              >
                App Store
              </IonButton>
              <IonButton
                expand="block"
                onClick={() =>
                  openStoreLink("https://play.google.com/store/apps/details?id=com.pipedream.werkapp&hl=en_US")
                }
                style={{ "--background": "#1a1a1a", "--color": "#ffffff" }}
              >
                Google Play
              </IonButton>
              <IonButton
                expand="block"
                fill="clear"
                onClick={() => setShowDownloadModal(false)}
                style={{ "--color": "#666" }}
              >
                Close
              </IonButton>
            </div>
          </div>
        </IonModal>
        <style>
          {`
            /* Responsive styles for website pages */
            .download-app-modal {
              --width: min(420px, calc(100% - 2rem));
              --height: auto;
              --border-radius: 12px;
            }

            @media (max-width: 768px) {
              .ion-page ion-toolbar ion-title {
                font-size: 1.2rem !important;
              }
              .ion-page ion-toolbar ion-buttons ion-button {
                font-size: 0.85rem !important;
                padding: 0 8px !important;
              }
              .get-app-label {
                display: none !important;
              }
              .get-app-short-label {
                display: inline-flex !important;
                font-size: 0.9rem !important;
                font-weight: 600 !important;
              }
              .get-app-header-button {
                min-width: 42px !important;
              }
              /* Hide portal link on mobile */
              .desktop-only-portal-link {
                display: none !important;
              }
            }

            .get-app-short-label {
              display: none;
            }
            
            @media (max-width: 480px) {
              .ion-page ion-toolbar ion-title {
                font-size: 1rem !important;
              }
            }
          `}
        </style>
        <div style={{ minHeight: "calc(100vh - 200px)" }}>{children}</div>

        {/* Footer */}
        <footer
          style={{
            backgroundColor: "#1a1a1a",
            color: "#ffffff",
            padding: "2rem 1rem",
            marginTop: "3rem",
            textAlign: "center",
            borderTop: "1px solid #333",
          }}
        >
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            <div style={{ marginBottom: "1rem" }}>
              <span
                onClick={() => history.push("/about")}
                style={{
                  margin: "0 1rem",
                  cursor: "pointer",
                  color: "#f4b41a",
                  textDecoration: "none",
                }}
              >
                About Us
              </span>
              <span style={{ color: "#666" }}>|</span>
              <span
                onClick={() => history.push("/privacy")}
                style={{
                  margin: "0 1rem",
                  cursor: "pointer",
                  color: "#f4b41a",
                  textDecoration: "none",
                }}
              >
                Privacy Policy
              </span>
              <span style={{ color: "#666" }}>|</span>
              <span
                onClick={() => history.push("/")}
                style={{
                  margin: "0 1rem",
                  cursor: "pointer",
                  color: "#f4b41a",
                  textDecoration: "none",
                }}
              >
                Home
              </span>
            </div>
            <div style={{ color: "#999", fontSize: "0.9rem" }}>© 2026 WerkApp. All rights reserved.</div>
          </div>
        </footer>
      </IonContent>
    </IonPage>
  );
};

export default WebsiteLayout;
